import { Transition } from "@headlessui/react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid";
import { zodResolver } from "@hookform/resolvers/zod";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import axios from "axios";
import Cookies from "js-cookie";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";

import Logo from "../../assets/images/un_resume_logo.jpeg";
import { ApiService } from "../api/services.gen";
import { checkAndRedirectByRole } from "../utils/authUtils";
import { getUserDetails } from "../utils/getUserDetails";

import { useAlert } from "./AlertProvider";
import { signupSchema } from "./schemas/signupSchema";

export default function SbSignup() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const showAlert = useAlert();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(signupSchema),
    defaultValues: {
      username: "",
      email: "",
      password: "",
      role: "",
    },
  });

  useEffect(() => {
    const verifySession = async () => {
      const id = Cookies.get("id");
      const role = Cookies.get("role");
      if (id || role) {
        try {
          await getUserDetails();
          checkAndRedirectByRole(navigate);
        } catch (error) {
          Cookies.remove("id");
          Cookies.remove("role");
          Cookies.remove("csrftoken");
        }
      }
    };
    verifySession();
  }, [navigate]);

  const onSubmit = async ({ username, email, password, role }) => {
    setLoading(true);
    try {
      const payload = {
        requestBody: {
          email,
          password,
          name: username,
          role,
          phone_number: "",
          address: "",
        },
      };
      await ApiService.apiSignupCreate(payload);
      // After successful signup, log the user in
      const loginPayload = {
        requestBody: {
          email,
          password,
        },
      };
      const loginRes = await ApiService.apiLoginCreate(loginPayload);
      const userId = loginRes.id;

      Cookies.set("id", userId, { expires: 7 });
      Cookies.set("role", role, { expires: 7 });

      // Fetch user details to check for company
      const userRes = await ApiService.apiUsersRetrieve({ id: userId });
      const userRole = userRes.role;
      const { company } = userRes;
      if (
        (userRole === "smallbiz_owner" || userRole === "nonprofit") &&
        !company
      ) {
        navigate("/orgprofile");
      } else if (userRole === "candidate") {
        navigate("/home");
      } else {
        navigate("/orghome");
      }
    } catch (error) {
      console.error("Signup error:", error);
      showAlert("Failed to signup", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleShowPassword = () => setShowPassword((prev) => !prev);

  const handleGoogleSuccess = async (response) => {
    if (!role) {
      showAlert("Please select a role before signing up with Google.", "error");
      return;
    }
    try {
      const csrfToken = Cookies.get("csrftoken");
      const payload = { token: response.credential, role };
      const res = await axios.post("/api/googlelogin/", payload, {
        headers: {
          "X-CSRFToken": csrfToken,
          "Content-Type": "application/json",
        },
        withCredentials: true,
      });

      if (res.status === 200 && res.data.user?.id) {
        const userId = res.data.user.id;
        Cookies.set("id", userId, { expires: 7 });

        const userRes = await ApiService.apiUsersRetrieve({ id: userId });
        const { role } = userRes;
        const { company } = userRes;
        Cookies.set("role", role, { expires: 7 });

        if ((role === "smallbiz_owner" || role === "nonprofit") && !company) {
          navigate("/orgprofile");
        } else if (role === "candidate") {
          navigate("/home");
        } else {
          navigate("/orghome");
        }
      } else {
        showAlert("Failed to sign up with Google", "error");
      }
    } catch (error) {
      showAlert("Google signup failed", "error");
    }
  };

  const handleGoogleFailure = () => showAlert("Google signup failed", "error");

  return (
    <GoogleOAuthProvider clientId="637231094750-2iviubrnmb70qmt9g89nr1nb1u6cqd43.apps.googleusercontent.com">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-950 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        <Transition
          appear
          enter="transition-all duration-700 ease-out"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          show
        >
          <div className="flex flex-col md:flex-row w-full max-w-4xl bg-white dark:bg-gray-800 rounded-3xl shadow-xl overflow-hidden">
            {/* Left Container */}
            <div className="flex flex-col items-center justify-center bg-gradient-to-br from-blue-400 to-blue-600 w-full md:w-2/5 text-white p-8 md:p-10">
              <img
                alt="UnResume Logo"
                className="w-28 h-28 rounded-full shadow-md mb-6"
                src={Logo}
              />
              <h2 className="text-3xl font-bold tracking-tight">UnResume</h2>
            </div>

            {/* Right Container */}
            <div className="flex flex-col p-6 md:p-10 w-full md:w-3/5">
              <h3 className="text-2xl font-semibold text-gray-800 dark:text-gray-100 text-center">
                Create Account
              </h3>

              {/* Google Signup */}
              <div className="mt-6 flex justify-center">
                <GoogleLogin
                  shape="pill"
                  size="large"
                  text="signup_with"
                  theme="filled_blue"
                  width="300"
                  onError={handleGoogleFailure}
                  onSuccess={handleGoogleSuccess}
                />
              </div>
              <div className="relative flex items-center justify-center my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300 dark:border-gray-600" />
                </div>
                <span className="relative bg-white dark:bg-gray-800 px-3 text-sm text-gray-500 dark:text-gray-400">
                  or
                </span>
              </div>

              {/* Form */}
              <form
                className="flex flex-col gap-6"
                onSubmit={handleSubmit(onSubmit)}
              >
                {/* Full Name */}
                <div className="relative">
                  <input
                    {...register("username")}
                    className="w-full px-4 py-3 rounded-full border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring focus:ring-blue-500 transition-all duration-200 bg-gray-50/50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                    placeholder="Full Name"
                  />
                  {errors.username && (
                    <p className="text-red-500 dark:text-red-400 text-sm mt-1">
                      {errors.username.message}
                    </p>
                  )}
                </div>
                <div className="relative">
                  <input
                    {...register("email")}
                    className="w-full px-4 py-3 rounded-full border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring focus:ring-blue-500 transition-all duration-200 bg-gray-50/50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                    placeholder="Email"
                  />
                  {errors.email && (
                    <p className="text-red-500 dark:text-red-400 text-sm mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>
                <div className="relative">
                  <input
                    {...register("password")}
                    className="w-full px-4 py-3 rounded-full border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring focus:ring-blue-500 transition-all duration-200 bg-gray-50/50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 pr-10"
                    placeholder="Password"
                    type={showPassword ? "text" : "password"}
                  />
                  <button
                    className="absolute right-3 top-6 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors duration-200"
                    type="button"
                    onClick={toggleShowPassword}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="w-5 h-5" />
                    ) : (
                      <EyeIcon className="w-5 h-5" />
                    )}
                  </button>
                  {errors.password && (
                    <p className="text-red-500 dark:text-red-400 text-sm mt-1">
                      {errors.password.message}
                    </p>
                  )}
                </div>
                {/* Role Dropdown - moved below other fields */}
                <div className="relative">
                  <select
                    {...register("role")}
                    className="w-full px-4 py-3 rounded-full border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring focus:ring-blue-500 transition-all duration-200 bg-gray-50/50 dark:bg-gray-900 text-gray-700 dark:text-gray-200"
                    name="role"
                  >
                    <option disabled value="">
                      Select Role
                    </option>
                    <option value="smallbiz_owner">Small Business Owner</option>
                    <option value="nonprofit">Nonprofit Organization</option>
                    <option value="nonprofit">Community Organizer</option>
                  </select>
                  {errors.role && (
                    <p className="text-red-500 dark:text-red-400 text-sm mt-1">
                      {errors.role.message}
                    </p>
                  )}
                </div>

                <button
                  className="w-full cursor-pointer py-3 bg-blue-500 text-white font-semibold rounded-full shadow-md hover:bg-blue-600 disabled:bg-blue-300 transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  disabled={loading}
                  type="submit"
                >
                  {loading ? "Signing Up..." : "Sign Up"}
                </button>
              </form>
              <p className="mt-4 text-sm text-gray-600 dark:text-gray-400 text-center">
                Already a user?{" "}
                <Link
                  className="text-blue-500 dark:text-blue-400 font-semibold hover:text-blue-600 dark:hover:text-blue-300 transition-colors duration-200"
                  to="/login"
                >
                  Log In
                </Link>
              </p>
            </div>
          </div>
        </Transition>
      </div>
    </GoogleOAuthProvider>
  );
}
